import { useState, useCallback, useEffect } from 'react';

/**
 * Hook to access browser geolocation API
 * @param {Object} options - Geolocation options
 * @param {boolean} options.autoRequest - Whether to request location on mount
 * @param {number} options.timeout - Timeout in ms (default 10000)
 * @param {boolean} options.enableHighAccuracy - Use high accuracy mode
 * @returns {Object} - Geolocation state and methods
 */
export const useGeolocation = (options = {}) => {
  const {
    autoRequest = false,
    timeout = 10000,
    enableHighAccuracy = true,
  } = options;

  const [state, setState] = useState({
    loading: false,
    error: null,
    position: null,
    // Formatted coordinates
    coordinates: null,
  });

  /**
   * Check if geolocation is supported
   */
  const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  /**
   * Request current position
   */
  const requestLocation = useCallback(() => {
    if (!isSupported) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        loading: false,
      }));
      return Promise.reject(new Error('Geolocation not supported'));
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          setState({
            loading: false,
            error: null,
            position,
            coordinates,
          });

          resolve(coordinates);
        },
        (error) => {
          let errorMessage;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              errorMessage = 'An unknown error occurred';
          }

          setState((prev) => ({
            ...prev,
            loading: false,
            error: errorMessage,
          }));

          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy,
          timeout,
          maximumAge: 60000, // Cache position for 1 minute
        }
      );
    });
  }, [isSupported, enableHighAccuracy, timeout]);

  /**
   * Clear the current position
   */
  const clearLocation = useCallback(() => {
    setState({
      loading: false,
      error: null,
      position: null,
      coordinates: null,
    });
  }, []);

  // Auto-request location on mount if enabled
  useEffect(() => {
    if (autoRequest && isSupported) {
      requestLocation().catch(() => {
        // Error already handled in state
      });
    }
  }, [autoRequest, isSupported, requestLocation]);

  return {
    ...state,
    isSupported,
    requestLocation,
    clearLocation,
  };
};

export default useGeolocation;
